import { type FC, useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod/dist/zod.js'
import { useKeycloak } from '@react-keycloak/web'
import { isEmpty } from 'lodash'
import isEqual from 'lodash/isEqual'
import * as z from 'zod'

import { Avatar, Body, Button, Divider, TextInput } from '@foxino/components-web'
import { Save01 } from '@foxino/icons-react'

import { Constants } from '@app/config/constants/Constants'
import type { GetUserProfileQuery, UserProfileInput } from '@app/data'
import { languageCodeToShort, useTranslation } from '@app/locales'
import i18n from '@app/locales/i18n'
import { getFullName } from '@app/modules/common/utils'

type ProfileFormValues = {
  firstName: string
  lastName: string
  email: string
  pictureUrl: string | undefined
}

type Props = {
  userProfile: GetUserProfileQuery['userProfile']
  isPending: boolean
  onSubmit: (data: UserProfileInput) => Promise<void>
}

export const ProfileForm: FC<Props> = ({ userProfile, isPending, onSubmit }: Props) => {
  const { t } = useTranslation('settings')
  const { keycloak } = useKeycloak()

  const defaultValues = useMemo<ProfileFormValues>(() => {
    return {
      firstName: userProfile.firstName,
      lastName: userProfile.lastName,
      email: userProfile.email || '',
      pictureUrl: userProfile.pictureUrl || undefined
    }
  }, [userProfile])

  const {
    handleSubmit,
    reset,
    register,
    formState: { dirtyFields, errors }
  } = useForm<ProfileFormValues>({ defaultValues, resolver: zodResolver(validationSchema) })

  if (!isEmpty(errors)) {
    console.info('ProfileForm - errors: ', errors)
  }

  useEffect(() => {
    reset(defaultValues)
  }, [defaultValues])

  const defaultFullName = getFullName(defaultValues.firstName, defaultValues.lastName)
  const isTouched = !isEqual(dirtyFields, {})

  const handleChangePasswordClick = () => {
    const keycloakServerUrl = keycloak.authServerUrl // Get the Keycloak server URL
    const realmName = keycloak.realm // Get the realm name
    const changePasswordUrl = `${keycloakServerUrl}/realms/${realmName}/protocol/openid-connect/auth/?response_type=code&client_id=${Constants.KEYCLOAK_CLIENT_ID}&kc_action=UPDATE_PASSWORD&redirect_uri=${window.location.origin}/settings&kc_locale=${languageCodeToShort[i18n.language]}` // Create the change password URL

    // Redirect the user to the change password page
    window.location.href = changePasswordUrl
  }

  const handleOnSubmit = async (data: ProfileFormValues) => {
    const { firstName, lastName, email } = data
    await onSubmit({ firstName, lastName, email })
  }

  return (
    <form onSubmit={handleSubmit(handleOnSubmit)} className="flex size-full flex-col p-5 text-white">
      <div className="flex items-center justify-between">
        <Body size="3xl" Component={'h3'} weight="bold">
          {t('profile.title')}
        </Body>
        <div className="flex items-center gap-2">
          <Button color="secondary" onClick={() => reset()}>
            {t('cancel')}
          </Button>
          <Button LeadingIcon={Save01} disabled={!isTouched || isPending} loading={isPending} type="submit">
            {t('save')}
          </Button>
        </div>
      </div>
      <Divider className="my-4 bg-primary-800" />
      {/* Avatar */}
      <div className="flex items-center gap-4">
        <Avatar
          size="2xl"
          src={defaultValues.pictureUrl ?? undefined}
          showTooltip
          fullName={defaultFullName}
          className="size-[65px] min-w-[65px] rounded-[10px]"
        />
        <div className="flex flex-1 flex-col gap-4">
          <Body size="xl" weight="bold" className="text-xl">
            {defaultFullName}
          </Body>
        </div>
      </div>
      <div className="mt-6 flex flex-col gap-4">
        {/* Names */}
        <div className="flex gap-4">
          <TextInput
            {...register('firstName')}
            label={t('profile.firstName')}
            placeholder={t('profile.firstName')}
            className="flex-1"
          />
          <TextInput
            {...register('lastName')}
            label={t('profile.lastName')}
            placeholder={t('profile.lastName')}
            className="flex-1"
          />
        </div>

        {/* Email */}
        <div className="flex gap-4">
          <TextInput
            {...register('email')}
            label={t('profile.email')}
            placeholder={t('profile.email')}
            className="flex-1"
            disabled
          />
        </div>

        {/* Password */}
        <div className="w-full">
          <Body Component="label" weight="semibold" className="mb-1 block">
            {t('profile.changePasswordLabel')}
          </Body>
          <Button onClick={handleChangePasswordClick}>{t('profile.changePassword')}</Button>
        </div>
      </div>
    </form>
  )
}

const validationSchema = z.object({
  firstName: z.string().min(2).max(100),
  lastName: z.string().min(2).max(100),
  email: z.string().email()
})
